// ImageKit utility functions for optimized image delivery with proper caching
// Based on ImageKit CDN caching best practices: https://imagekit.io/docs/cdn-caching-and-purging

/**
 * Cache configuration for different types of ImageKit images
 */
export const IMAGEKIT_CACHE_CONFIG = {
  // Shop icons are relatively static, cache for 24 hours
  shopIcons: {
    maxAge: 86400, // 24 hours in seconds
    staleWhileRevalidate: 604800, // 7 days in seconds
  },
  // General images cache for 1 hour with longer stale period
  general: {
    maxAge: 3600, // 1 hour in seconds
    staleWhileRevalidate: 86400, // 24 hours in seconds
  },
} as const;

interface ImageKitTransformOptions {
  width?: number;
  height?: number;
  quality?: number | 'auto';
  format?: string | 'auto';
  crop?: 'maintain_ratio' | 'force' | 'at_least' | 'at_max';
  focus?: 'auto' | 'face' | 'center';
  progressive?: boolean;
  lossless?: boolean;
}

/**
 * Generates an optimized ImageKit URL with proper transformation parameters
 * for better caching and performance
 * 
 * @param imageUrl - The original ImageKit image URL
 * @param options - Transformation options for optimization
 * @returns Optimized ImageKit URL with transformation parameters
 */
export function getOptimizedImageKitUrl(
  imageUrl: string, 
  options: ImageKitTransformOptions = {}
): string {
  if (!imageUrl || !imageUrl.includes('ik.imagekit.io')) {
    return imageUrl; // Return original URL if not an ImageKit URL
  }

  const {
    width,
    height,
    quality = 'auto', // Use auto quality for optimal compression
    format = 'auto',   // Use auto format for modern format delivery (WebP, AVIF)
    crop = 'maintain_ratio',
    focus = 'auto',
    progressive = true,
    lossless = false
  } = options;

  // Build transformation parameters
  const transformations: string[] = [];

  // Format optimization - critical for caching efficiency
  if (format) {
    transformations.push(`f-${format}`);
  }

  // Quality optimization - reduces file size while maintaining visual quality
  if (quality) {
    transformations.push(`q-${quality}`);
  }

  // Responsive sizing - serve appropriately sized images
  if (width) {
    transformations.push(`w-${width}`);
  }
  if (height) {
    transformations.push(`h-${height}`);
  }

  // Cropping and focus
  if (crop && (width || height)) {
    transformations.push(`c-${crop}`);
  }
  if (focus && crop !== 'force') {
    transformations.push(`fo-${focus}`);
  }

  // Progressive loading for better perceived performance
  if (progressive) {
    transformations.push('pr-true');
  }

  // Lossless compression if needed
  if (lossless) {
    transformations.push('lo-true');
  }

  // If no transformations, return original URL
  if (transformations.length === 0) {
    return imageUrl;
  }

  // Add transformation parameters to URL
  const transformationString = transformations.join(',');
  
  // Check if URL already has transformation parameters
  if (imageUrl.includes('?tr=')) {
    // Append to existing transformations
    return imageUrl.replace('?tr=', `?tr=${transformationString},`);
  } else {
    // Add new transformation parameters
    const separator = imageUrl.includes('?') ? '&' : '?';
    return `${imageUrl}${separator}tr=${transformationString}`;
  }
}

/**
 * Optimizes shop icon URLs for different use cases
 */
export const getOptimizedShopIcon = {
  /**
   * For table/list view - small icons (32x32)
   */
  small: (iconUrl: string) => getOptimizedImageKitUrl(iconUrl, {
    width: 32,
    height: 32,
    quality: 'auto',
    format: 'auto',
    crop: 'maintain_ratio',
    focus: 'auto'
  }),

  /**
   * For map markers - medium icons (48x48)
   */
  medium: (iconUrl: string) => getOptimizedImageKitUrl(iconUrl, {
    width: 48,
    height: 48,
    quality: 'auto',
    format: 'auto',
    crop: 'maintain_ratio',
    focus: 'auto'
  }),

  /**
   * For admin preview - larger icons (96x96)
   */
  large: (iconUrl: string) => getOptimizedImageKitUrl(iconUrl, {
    width: 96,
    height: 96,
    quality: 'auto',
    format: 'auto',
    crop: 'maintain_ratio',
    focus: 'auto'
  }),

  /**
   * For high-DPI displays - 2x resolution
   */
  retina: (iconUrl: string, size: number = 32) => getOptimizedImageKitUrl(iconUrl, {
    width: size * 2,
    height: size * 2,
    quality: 'auto',
    format: 'auto',
    crop: 'maintain_ratio',
    focus: 'auto'
  })
};

/**
 * Checks if a URL is an ImageKit URL
 */
export function isImageKitUrl(url: string): boolean {
  return Boolean(url) && url.includes('ik.imagekit.io');
}

/**
 * Generates a srcset for responsive images using ImageKit
 */
export function generateImageKitSrcSet(
  imageUrl: string, 
  sizes: number[] = [32, 48, 64, 96]
): string {
  if (!isImageKitUrl(imageUrl)) {
    return imageUrl;
  }

  return sizes
    .map(size => `${getOptimizedImageKitUrl(imageUrl, { 
      width: size, 
      height: size, 
      quality: 'auto', 
      format: 'auto',
      crop: 'force'
    })} ${size}w`)
    .join(', ');
}

/**
 * Generate Cache-Control header value for ImageKit images
 * @param type - Type of image (shopIcons, general)
 * @returns Cache-Control header value
 */
export function getImageKitCacheHeaders(type: keyof typeof IMAGEKIT_CACHE_CONFIG = 'general'): string {
  const config = IMAGEKIT_CACHE_CONFIG[type];
  return `public, max-age=${config.maxAge}, stale-while-revalidate=${config.staleWhileRevalidate}`;
}

/**
 * Get recommended cache headers for any image URL
 * @param url - Image URL
 * @param type - Type of image for cache configuration
 * @returns Object with cache headers
 */
export function getImageCacheHeaders(url: string, type: keyof typeof IMAGEKIT_CACHE_CONFIG = 'general') {
  if (isImageKitUrl(url)) {
    return {
      'Cache-Control': getImageKitCacheHeaders(type),
      'X-Cache-Source': 'ImageKit-CDN',
    };
  }
  
  // For non-ImageKit images, use more conservative caching
  return {
    'Cache-Control': 'public, max-age=3600, stale-while-revalidate=86400',
    'X-Cache-Source': 'External',
  };
}

/**
 * ImageKit CDN Caching Documentation
 * 
 * ImageKit automatically handles comprehensive caching at multiple levels:
 * 
 * 1. **Browser Caching**:
 *    - Automatic Cache-Control headers for optimal browser caching
 *    - ETag support for conditional requests
 *    - Last-Modified headers for cache validation
 * 
 * 2. **CDN Edge Caching**:
 *    - Global edge locations for fast content delivery
 *    - Automatic cache invalidation when images are updated
 *    - Smart cache purging based on transformation parameters
 * 
 * 3. **Transformation Caching**:
 *    - Real-time transformations are cached at the edge
 *    - Each unique transformation URL is cached separately
 *    - Automatic format optimization (WebP, AVIF) with caching
 * 
 * 4. **Our Implementation**:
 *    - Shop icons: 24h cache (static content, rarely changes)
 *    - General images: 1h cache (may change more frequently)
 *    - Stale-while-revalidate for better UX during cache updates
 * 
 * 5. **Performance Benefits**:
 *    - Reduced bandwidth usage through compression and format optimization
 *    - Faster load times via global CDN distribution
 *    - Automatic responsive image delivery based on device capabilities
 *    - Progressive loading with lazy loading support
 * 
 * 6. **Cache Invalidation**:
 *    - ImageKit handles automatic cache purging when images are updated
 *    - Manual cache purging available through ImageKit dashboard
 *    - Version-based URLs ensure cache busting when needed
 * 
 * For more details, see: https://imagekit.io/docs/cdn-caching-and-purging
 */